import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import PolicyCTA from "@/components/policy/PolicyCTA";
import PolicyHero from "@/components/policy/PolicyHero";
import RevealStyles from "@/components/policy/RevealStyles";
import {
  Scale,
  Shield,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  DollarSign,
  XCircle,
  FileText,
  Building2,
  Briefcase,
  Home,
  Heart,
  Clock,
  Eye,
  TrendingUp,
  BarChart3,
  Unlock,
  AlertTriangle,
  Landmark,
  Database,
} from "lucide-react";

/* ── FAQ ── */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-white/[0.03]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
      >
        <span className="font-bold text-white pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/30 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-white/50 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

/* ── Animated Counter ── */
const AnimCounter = ({ end, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1500;
          const startTime = performance.now();
          const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

/* ── Policy Card ── */
const PolicyCard = ({ icon: Icon, title, summary, details, callout }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white/[0.03] border border-white/10 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
            <p className="text-white/50 text-sm leading-relaxed">{summary}</p>
          </div>
        </div>
        {callout && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 p-4 mb-4">
            <p className="text-[#D4AF37] font-bold text-sm">"{callout}"</p>
          </div>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:text-[#F5D76E] transition-colors"
        >
          {expanded ? "Show Less" : "Read More"}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      {expanded && (
        <div className="px-6 pb-6 border-t border-white/5 pt-4">
          <div className="text-white/50 text-sm leading-relaxed space-y-3">
            {details.map((d, i) => (
              <p key={`${title}-${i}`}>{d}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const faqs = [
  { question: "Does this erase convictions?", answer: "No. Record sealing means the record is hidden from most public searches, but it still exists in the justice system. Law enforcement and courts can still access sealed records when legally required. This is about removing unnecessary barriers — not rewriting history." },
  { question: "Will violent crimes be automatically sealed?", answer: "No. This plan includes serious public safety exceptions. Certain sex offenses, crimes against children, felony DUI, and other offenses already excluded under Nevada law will remain excluded from automatic sealing. Reform and public safety coexist in this plan." },
  { question: "Does this apply to dismissed charges?", answer: "Yes. If the state did not convict someone — meaning charges were dismissed, declined, or ended in acquittal — the record should not follow them indefinitely. Being accused is not the same as being guilty." },
  { question: "How long are waiting periods?", answer: "Waiting periods vary based on offense type. Misdemeanors typically have shorter waiting periods than felonies. During this time, the individual must remain conviction-free, complete all terms of their sentence, and fulfill any restitution obligations." },
  { question: "Can employers still see sealed records?", answer: "Generally, no. Once sealed, employers should not have access to sealed records for most positions. Exceptions exist for certain sensitive positions as defined by law, such as law enforcement or positions working with vulnerable populations." },
  { question: "Does this make Nevada less safe?", answer: "No. Research consistently shows that people with stable housing, employment, and community connections are significantly less likely to reoffend. This plan strengthens public safety by supporting successful reintegration. Serious offenses remain excluded." },
  { question: "How much will implementation cost?", answer: "The upfront technology investment is offset by reduced court workload, reduced incarceration costs, increased tax revenue from expanded workforce participation, and decreased reliance on public assistance. Multiple states have found Clean Slate implementation to be cost-effective." },
  { question: "How does this help the economy?", answer: "Removing barriers to employment allows thousands of Nevadans to enter or re-enter the workforce. This means more tax revenue, more consumer spending, reduced public assistance costs, and a larger, more productive labor force — particularly valuable during labor shortages." },
];

const PostJusticePage = () => {
  useReveal();

  return (
    <div data-testid="post-justice-page" className="bg-[#0B1A3B]">
      {/* ── HERO ── */}
      <PolicyHero
        testId="post-justice-hero"
        badge={{ icon: Scale, label: "Post-Justice Reform" }}
        headline={
          <>
            The Nevada{" "}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
              Clean Slate Act
            </span>
          </>
        }
      >
        <p className="text-xl sm:text-2xl text-white/50 max-w-2xl mx-auto mb-10">
          Your future should not depend on whether you can afford an attorney.
        </p>

        <div className="flex flex-wrap justify-center gap-4 mb-16">
          <a href="#the-plan" className="group inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]">
            Read The Plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="#why-matters" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all backdrop-blur-sm">
            Restore Opportunity
          </a>
          <a href="#economic" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all backdrop-blur-sm">
            End Justice By Income
          </a>
        </div>

        {/* Stat Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { stat: "1 in 3", label: "Americans has some form of criminal record" },
            { stat: "50%+", label: "Reduction in job callbacks with a record" },
            { stat: "1000s", label: "Nevadans eligible but unable to complete sealing" },
            { stat: "$$$", label: "Legal costs many families cannot afford" },
          ].map((item) => (
            <div key={item.label} className="bg-white/[0.04] border border-white/[0.08] p-5 backdrop-blur-sm">
              <p className="text-2xl font-black text-[#D4AF37] mb-1">{item.stat}</p>
              <p className="text-white/40 text-xs leading-relaxed">{item.label}</p>
            </div>
          ))}
        </div>
      </PolicyHero>

      {/* ── OPENING MESSAGE ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              Nevada's Justice System Shouldn't Have a Price Tag
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-white/[0.04] border border-white/10 p-6">
              <h3 className="text-[#D4AF37] font-bold text-sm uppercase tracking-wider mb-4">If You Have Money</h3>
              <div className="space-y-3">
                {["Hire an attorney to navigate the system", "File petitions efficiently", "Clear records and restore opportunity faster", "Move on with your life"].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-red-500/[0.06] border border-red-500/20 p-6">
              <h3 className="text-red-400 font-bold text-sm uppercase tracking-wider mb-4">If You Don't</h3>
              <div className="space-y-3">
                {["Remain trapped by old records indefinitely", "Housing applications denied", "Job opportunities lost", "Families destabilized for years"].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="reveal bg-white/[0.04] border-2 border-[#D4AF37]/30 p-8 text-center">
            <p className="text-white font-bold text-lg leading-relaxed">
              "If someone is legally eligible to move on with their life, Nevada should not force them to{" "}
              <span className="text-[#D4AF37]">buy</span> that opportunity."
            </p>
          </div>
        </div>
      </section>

      {/* ── WHAT IS POST-JUSTICE REFORM ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Understanding the Framework</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              What Is Post-Justice Reform?
            </h2>
            <p className="text-white/40 mt-4 max-w-2xl mx-auto">
              Pre-trial reform addresses injustice before conviction. Post-justice reform addresses barriers after people have completed their sentence.
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-6">
            <div className="bg-white/[0.04] border border-white/10 p-6">
              <h3 className="text-[#D4AF37] font-bold mb-4">Pre-Trial Reform</h3>
              <div className="space-y-3">
                {["Ends wealth-based detention", "Reduces unnecessary incarceration", "Protects due process"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/60 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 p-6">
              <h3 className="text-[#D4AF37] font-bold mb-4">Post-Justice Reform</h3>
              <div className="space-y-3">
                {["Ends wealth-based record clearing", "Restores economic opportunity", "Supports reintegration and stability"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Unlock className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/80 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PLAN ── */}
      <section id="the-plan" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Nevada Clean Slate Act</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">The Plan</h2>
          </div>

          <div className="reveal space-y-5">
            <PolicyCard
              icon={FileText}
              title="Automatic Sealing for Dismissed Cases & Acquittals"
              summary="If the state does not convict someone, the record should not follow them indefinitely. Charges that are dismissed, declined, or end in acquittal should be automatically sealed."
              callout="Being accused is not the same as being guilty."
              details={[
                "Currently, even dismissed charges remain on a person's record and can appear in background checks, affecting employment, housing, and licensing opportunities.",
                "The petition process requires filing fees, attorney costs, complex paperwork, and navigating the court system — barriers that disproportionately affect working-class Nevadans.",
                "Under this plan, dismissed cases and acquittals would be automatically sealed without requiring any action from the individual.",
              ]}
            />
            <PolicyCard
              icon={Clock}
              title="Automatic Sealing After Waiting Periods"
              summary="Once a person completes their sentence, probation/parole, pays restitution, and remains conviction-free during the waiting period — the state automatically seals eligible records."
              details={[
                "The process follows a clear path: Sentence Completed → Waiting Period Satisfied → Automatic Sealing → Opportunity Restored.",
                "No petition required. No attorney required. No filing fees. The system does the work.",
                "Waiting periods vary based on offense severity, ensuring appropriate time has passed while eliminating unnecessary bureaucratic barriers.",
              ]}
            />
            <PolicyCard
              icon={DollarSign}
              title="End Justice By Income"
              summary="Attorneys cost money. Certified records cost money. Filing systems are confusing. Missing work for court appearances costs money. The wealthy recover faster because they can pay for recovery."
              callout="A second chance should not be reserved for people who can afford one."
              details={[
                "The current system creates a two-tier justice outcome: those who can afford legal help move on, and those who can't remain trapped.",
                "This is the same structural inequality as money bail — opportunity should not depend on wealth.",
                "Automatic sealing eliminates the financial barrier entirely, ensuring equal access to record clearing for all eligible Nevadans.",
              ]}
            />
            <PolicyCard
              icon={Shield}
              title="Serious Public Safety Exceptions Remain"
              summary="This plan does NOT automatically seal certain sex offenses, crimes against children, felony DUI exclusions, and other offenses already excluded under Nevada law."
              callout="Reform and public safety can exist together."
              details={[
                "Public safety is not compromised by this plan. Serious offenses remain excluded from automatic sealing, consistent with existing Nevada law.",
                "The focus is on removing barriers for people who have genuinely completed their obligations and demonstrated rehabilitation.",
                "This balanced approach maintains community trust while expanding opportunity for those who have earned it.",
              ]}
            />
            <PolicyCard
              icon={Database}
              title="Modernize Nevada's Record System"
              summary="Courts, law enforcement, prosecutors, and state databases should coordinate electronically to enable automation, faster processing, and reduced bureaucracy."
              details={[
                "The current system is fragmented — different agencies maintain separate records with limited coordination, creating delays and errors.",
                "A modernized system enables automatic eligibility verification, real-time record updates, and seamless processing across all agencies.",
                "This reduces court backlog, frees judicial resources, and ensures records are updated consistently and promptly statewide.",
              ]}
            />
            <PolicyCard
              icon={Home}
              title="Housing & Employment Protections"
              summary="Once sealed, employers should generally not use sealed records, landlords should generally not use sealed records, and licensing boards should have stricter limitations."
              details={[
                "Sealed records should mean sealed — not available through third-party background check companies or informal channels.",
                "Employment protections mean more Nevadans working, paying taxes, and contributing to the economy instead of being locked out of opportunity.",
                "Housing protections reduce homelessness risk and stabilize families, creating stronger communities and reducing downstream public costs.",
              ]}
            />
            <PolicyCard
              icon={Eye}
              title="Transparency & Accountability"
              summary="Annual public reporting on records sealed, processing timelines, denial reasons, economic outcomes, geographic data, and demographic impact."
              details={[
                "Accountability ensures the system actually works. Without data, there is no way to measure success or identify problems.",
                "Public reporting creates trust, enables oversight, and allows continuous improvement of the process.",
                "Geographic and demographic data helps identify disparities and ensures the system serves all Nevadans equitably.",
              ]}
            />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Process</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">How It Works</h2>
          </div>

          <div className="reveal relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-[#D4AF37]/20" />
            {[
              { icon: CheckCircle, title: "Sentence Completed", desc: "Individual completes all terms of their sentence, including probation, parole, and restitution" },
              { icon: Clock, title: "Waiting Period Satisfied", desc: "Required time passes with no new convictions" },
              { icon: Database, title: "State System Verifies Eligibility", desc: "Automated check confirms all requirements are met" },
              { icon: Unlock, title: "Automatic Sealing Triggered", desc: "No petition, no attorney, no fees — the system acts" },
              { icon: FileText, title: "Confirmation Sent", desc: "Individual receives notification that records have been sealed" },
              { icon: Shield, title: "Records Updated Across Agencies", desc: "All state databases reflect the sealed status" },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="relative flex items-start gap-6 mb-8 last:mb-0">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-black text-sm z-10">
                    {i + 1}
                  </div>
                  <div className="pt-2">
                    <h3 className="text-white font-bold mb-1">{step.title}</h3>
                    <p className="text-white/40 text-sm">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHY THIS MATTERS ── */}
      <section id="why-matters" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Impact</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Why This Matters</h2>
          </div>

          <div className="reveal grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Briefcase, title: "Employment Barriers", stat: 50, suffix: "%+", desc: "Reduction in callback rates for applicants with records" },
              { icon: Home, title: "Housing Barriers", stat: 80, suffix: "%", desc: "Of landlords conduct background checks that surface old records" },
              { icon: DollarSign, title: "Reduced Wages", stat: 22, suffix: "%", desc: "Average wage reduction for workers with criminal records" },
              { icon: Heart, title: "Family Instability", stat: 5, suffix: "M+", desc: "Children have a parent with a criminal record nationwide" },
              { icon: TrendingUp, title: "Recidivism Reduction", stat: 11, suffix: "%", desc: "Lower re-arrest rates when records are cleared" },
              { icon: Users, title: "Workforce Participation", stat: 30, suffix: "%", desc: "Increase in employment after record sealing" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/[0.04] border border-white/[0.08] p-6">
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <p className="text-3xl font-black text-white mb-1">
                    <AnimCounter end={item.stat} suffix={item.suffix} />
                  </p>
                  <p className="text-white font-bold text-sm mb-1">{item.title}</p>
                  <p className="text-white/40 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ECONOMIC BENEFITS ── */}
      <section id="economic" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Economic Impact</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              Second Chances Strengthen Nevada's Economy
            </h2>
            <p className="text-white/40 max-w-2xl mx-auto">
              This is not just justice reform. This is workforce reform. This is economic reform.
            </p>
          </div>

          <div className="reveal grid sm:grid-cols-2 gap-4">
            {[
              { icon: Users, title: "Address Labor Shortages", desc: "Expand the eligible workforce by removing unnecessary barriers to employment" },
              { icon: Briefcase, title: "Skilled Workforce Expansion", desc: "Unlock talent currently locked out of licensed professions and career paths" },
              { icon: TrendingUp, title: "Tax Revenue Increases", desc: "More workers means more income tax, sales tax, and economic activity" },
              { icon: DollarSign, title: "Reduced Public Costs", desc: "Less incarceration, less homelessness, less public assistance dependency" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/[0.04] border border-white/10 p-6">
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PUBLIC SAFETY ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <Shield className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
              Stability Reduces Crime
            </h2>
            <p className="text-white/40 max-w-2xl mx-auto">
              People with stable housing, employment, education, and opportunity are significantly less likely to reoffend. Reintegration improves public safety.
            </p>
          </div>

          <div className="reveal grid sm:grid-cols-2 gap-4">
            {[
              "Stable employment reduces recidivism",
              "Stable housing reduces homelessness and related offenses",
              "Economic participation builds community investment",
              "Family stability reduces intergenerational justice involvement",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-white/10 p-5">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm">{item}</span>
              </div>
            ))}
          </div>

          <div className="reveal mt-8 bg-white/[0.04] border-2 border-[#D4AF37]/20 p-6 text-center">
            <p className="text-white font-bold">
              "The safest communities are the ones where people have something to lose."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Frequently Asked Questions</h2>
          </div>
          <div className="reveal space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── RESEARCH ── */}
      <section className="relative py-16">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 text-center">Research & Legal Foundation</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Nevada Revised Statutes",
                "Brennan Center for Justice",
                "National Employment Law Project",
                "Clean Slate Initiative",
                "Bureau of Justice Statistics",
                "U.S. Department of Justice",
              ].map((source) => (
                <span key={source} className="px-4 py-2 bg-white/[0.04] border border-white/10 text-white/40 text-xs font-medium">
                  {source}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <PolicyCTA
        testId="post-justice-cta"
        heading="Justice Should Not Last Forever"
        subtitle="If someone has completed their sentence, followed the law, and earned a second chance, Nevada should not force them to buy that future."
        primary={{ label: "Read More Solutions", to: "/policies/more-coming-soon" }}
        secondary={{ label: "Volunteer", to: "/volunteer" }}
        tertiary={{ label: "Contact the Campaign", to: "/contact" }}
        next={{ label: "Pretrial Justice Reform", to: "/policies/bail-reform" }}
      />

      {/* Reveal animation styles */}
      <RevealStyles />
    </div>
  );
};

export default PostJusticePage;
